/**
 * 多格式导出（8.4）。
 *
 * 支持 JSON / CSS / React / Lottie 四种格式；Lottie 由 lottie.ts 实现
 * （keyframes 型配方的 opacity/transform 关键帧 → Lottie v5.x JSON，
 * transition 型与 box-shadow 等属性明确报错）。
 */
import type { CSSProperties } from "react";

import { applyRecipe, cssAnimationName } from "./engine";
import { recipeToLottie } from "./lottie";
import type { MotionRecipe, RecipeConfig } from "./types";

export type ExportFormat = "json" | "css" | "react" | "lottie";

const TRIGGER_PSEUDO: Partial<Record<RecipeConfig["trigger"], string>> = {
  hover: ":hover",
  focus: ":focus",
  click: ":active",
};

function animationShorthand(recipe: MotionRecipe, cfg: RecipeConfig): string {
  return `${cssAnimationName(recipe.name)} ${cfg.duration}ms ${cfg.easing} ${cfg.delay}ms ${
    cfg.iterationCount ?? 1
  } ${cfg.fillMode ?? "both"}`;
}

function renderKeyframes(recipe: MotionRecipe, cfg: RecipeConfig): string {
  const lines = [`@keyframes ${cssAnimationName(recipe.name)} {`];
  for (const [step, declarations] of Object.entries(cfg.keyframes ?? {})) {
    lines.push(`  ${step} {`);
    for (const [prop, value] of Object.entries(declarations)) {
      lines.push(`    ${prop}: ${value};`);
    }
    lines.push(`  }`);
  }
  lines.push(`}`);
  return lines.join("\n");
}

/** 导出单个配方为 CSS 片段（@keyframes + 类规则）。 */
export function recipeToCss(recipe: MotionRecipe): string {
  const cfg = recipe.recipe;
  const recipeSelector = `.dp-motion-${recipe.name}`;
  const triggerSelector = `.dp-motion-trigger-${cfg.trigger}`;
  /** 选择器用点连接（同一元素同时持有两个类），不能复用空格连接的 motionClassName。 */
  const scoped = `${recipeSelector}${triggerSelector}`;
  const parts: string[] = [];

  if (cfg.kind === "keyframes") {
    parts.push(renderKeyframes(recipe, cfg));

    if (cfg.trigger === "error") {
      // 错误类触发没有 CSS 伪类：由 JS 在错误态给元素加上 trigger 类即播放。
      parts.push(
        `${recipeSelector} {\n  animation: none;\n}\n` +
          `/* 错误态：给元素添加 ${scoped} 类时播放 */\n` +
          `${scoped} {\n  animation: ${animationShorthand(recipe, cfg)};\n}`,
      );
    } else if (TRIGGER_PSEUDO[cfg.trigger]) {
      parts.push(
        `${recipeSelector} {\n  animation: none;\n}\n` +
          `${scoped}${TRIGGER_PSEUDO[cfg.trigger]} {\n  animation: ${animationShorthand(recipe, cfg)};\n}`,
      );
    } else {
      // load / scroll-into-view / navigation：类出现即播放（导航时机由应用层控制）。
      parts.push(
        `${recipeSelector} {\n  animation: ${animationShorthand(recipe, cfg)};\n}` +
          (cfg.trigger === "navigation" ? "\n/* 导航出场：由页面切换逻辑在离场时挂上该类 */" : ""),
      );
    }
  } else {
    const t = cfg.transition;
    if (!t) return `${recipeSelector} {\n}`;
    const baseDecls = [`  transition: ${t.property} ${t.duration}ms ${t.timingFunction};`];
    if (cfg.from) {
      for (const [prop, value] of Object.entries(cfg.from)) baseDecls.push(`  ${prop}: ${value};`);
    }
    parts.push(`${recipeSelector} {\n${baseDecls.join("\n")}\n}`);
    if (cfg.trigger === "error") {
      parts.push(`/* 错误态：${scoped} 类存在时进入 ${JSON.stringify(cfg.to)} */`);
    } else if (TRIGGER_PSEUDO[cfg.trigger]) {
      const target = cfg.to
        ? Object.entries(cfg.to)
            .map(([prop, value]) => `${prop}: ${value};`)
            .join(" ")
        : "";
      parts.push(`${scoped}${TRIGGER_PSEUDO[cfg.trigger]} { ${target} }`);
    }
  }

  return parts.join("\n");
}

/** 把内联 style（camelCase 键）序列化成 React style={{...}} 字面量片段。 */
export function reactStyleLiteral(style: CSSProperties): string {
  const entries = Object.entries(style).map(
    ([key, value]) => `${key}: ${JSON.stringify(String(value))}`,
  );
  return `style={{ ${entries.join(", ")} }}`;
}

/** 导出单个配方为 React（内联 style 的 JSX 片段）。 */
export function recipeToReact(recipe: MotionRecipe): string {
  const applied = applyRecipe(recipe);
  const cfg = recipe.recipe;
  const className = applied.className ?? "";

  const attrs = [`className="${className}"`];
  if (applied.style && Object.keys(applied.style).length > 0) {
    attrs.push(reactStyleLiteral(applied.style));
  }

  const recipeClass = `dp-motion-${recipe.name}`;
  const triggerClass = `dp-motion-trigger-${cfg.trigger}`;
  const scoped = `${recipeClass}.${triggerClass}`;
  const hint =
    cfg.trigger === "error"
      ? `\n  {/* 错误态：给元素动态添加 "${scoped}" 类时播放 */}`
      : TRIGGER_PSEUDO[cfg.trigger]
        ? `\n  {/* 交互态：${scoped}${TRIGGER_PSEUDO[cfg.trigger]} 时播放，配合下方 CSS 片段 */}`
        : cfg.trigger === "navigation"
          ? `\n  {/* 导航出场：离场时挂上 ${recipeClass} 类触发 */}`
          : "";

  return `<div\n  ${attrs.join("\n  ")}>\n  {/* children */}\n</div>${hint}`;
}

/**
 * 导出配方为指定格式的文本。
 * - json:  跨平台动效数据（配方完整对象）
 * - css:   @keyframes + 类规则
 * - react: 内联 style 的 JSX 片段
 * - lottie: Lottie v5.x 动画 JSON（仅支持 opacity/transform 的 keyframes 配方；
 *          transition 型 / box-shadow 等抛 LottieUnsupportedError 并列出支持配方）
 */
export function exportRecipe(recipe: MotionRecipe, format: ExportFormat): string {
  switch (format) {
    case "json":
      return JSON.stringify(recipe, null, 2);
    case "css":
      return recipeToCss(recipe);
    case "react":
      return recipeToReact(recipe);
    case "lottie":
      return JSON.stringify(recipeToLottie(recipe), null, 2);
  }
}

/** 批量导出多个配方（css/react 以空行分隔；json/lottie 输出数组）。 */
export function exportRecipes(recipes: MotionRecipe[], format: ExportFormat): string {
  if (format === "json") return JSON.stringify(recipes, null, 2);
  if (format === "lottie") return JSON.stringify(recipes.map((recipe) => recipeToLottie(recipe)), null, 2);
  return recipes.map((recipe) => exportRecipe(recipe, format)).join("\n\n");
}
